import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';

export const ITEM_HEIGHT = 48;
export const ITEM_PADDING_TOP = 8;

/** Ms after last keystroke before product name search is applied to list fetch / cache (aligned with store search). */
export const PRODUCT_SEARCH_DEBOUNCE_MS = 700;

export const signupSelectMenuPaperSx = {
  '& .MuiMenuItem-root': {
    color: '#3F3F6E',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'capitalize',
    '&.Mui-selected': {
      backgroundColor: '#fdeee7 !important',
      '&:hover': {
        backgroundColor: '#fcddcf !important',
      },
    },
  },
};

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
    sx: signupSelectMenuPaperSx,
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

export const getStatusMenuProps = (isCompactLayout) => ({
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: isCompactLayout ? '100%' : '260px',
    },
    sx: {
      '& .MuiMenuItem-root': {
        color: '#3F3F6E',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'capitalize',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        '&.Mui-selected': {
          backgroundColor: '#fdeee7 !important',
          '&:hover': {
            backgroundColor: '#fcddcf !important',
          },
        },
      },
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
});

export const sharedInputSx = {
  marginBottom: 0,
  '& .MuiInputLabel-root': {
    color: '#3F3F6E',
    fontSize: '13px',
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#3F3F6E',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    color: '#3F3F6E',
    fontWeight: 500,
    paddingLeft: '4px',
  },
  '& .MuiOutlinedInput-root': {
    height: '42px',
    borderRadius: '5px',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderWidth: '1px',
      borderColor: '#D7E0F2',
    },
    '&:hover fieldset': {
      borderColor: '#3F3F6E',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3F3F6E',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3F3F6E !important',
    },
    '& input': {
      fontSize: '13px',
    },
    '& .MuiSelect-select': {
      fontSize: '13px',
      color: '#3F3F6E',
      textTransform: 'capitalize',
    },
    '& .MuiSelect-icon': {
      color: '#3F3F6E',
    },
  },
};

export const promotionLabels = {
  buy_one_get_one_free: 'Buy 1 Get 1 Free',
  buy_two_get_one_free: 'Buy 2 Get 1 Free',
  buy_one_get_half_price: 'Buy 1 Get Half Price',
};

export const CARD_CONTAINER_STYLE = {
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: '0px',
  marginBottom: '14px',
  gap: '12px',
  overflowX: 'visible',
  alignItems: 'stretch',
};

export const CARD_STYLE = {
  flex: '1 1 170px',
  minWidth: '170px',
  background: '#ffffff',
  border: '1px solid #d7e0f2',
  borderRadius: '10px',
  padding: '12px 14px',
  boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
  boxSizing: 'border-box',
  minHeight: '62px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  transition: 'transform 0.15s ease, box-shadow 0.2s ease',
};

export const LABEL_STYLE = {
  fontSize: '12px',
  color: '#64748b',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flex: 1,
};

export const VALUE_STYLE = {
  fontSize: '22px',
  fontWeight: 800,
  color: '#1e293b',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  marginLeft: '8px',
};

export const STATUS_STYLES = {
  approved: {
    color: '#2e7d32',
    Icon: CheckCircleRounded,
  },
  pending: {
    color: '#e65100',
    Icon: WarningAmberRounded,
  },
  rejected: {
    color: '#e53935',
    Icon: CancelRounded,
  },
};

export const EMPTY_PRODUCT_STAT_CARDS = [
  { label: 'Approved', value: 0, tone: 'approved' },
  { label: 'Merchant review pending', value: 0, tone: 'pending' },
  { label: 'Admin review pending', value: 0, tone: 'pending' },
  { label: 'Content review pending', value: 0, tone: 'pending' },
  { label: 'Image review pending', value: 0, tone: 'pending' },
  { label: 'Rejected', value: 0, tone: 'rejected' },
];

/** Aligned with GetProductsByManagingBranch `status_type` enum (lowercase snake_case). */
export const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'admin_review_pending', label: 'Admin review pending' },
  { value: 'admin_rejected', label: 'Admin rejected' },
  { value: 'merchant_approved', label: 'Merchant approved' },
  { value: 'merchant_rejected', label: 'Merchant rejected' },
  { value: 'merchant_review_pending', label: 'Merchant review pending' },
  {
    value: 'ai_generated_merchant_review_pending',
    label: 'AI generated merchant review pending',
  },
];
